import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { AssetDto } from '@picflow/types';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { fixContentEditableStyles } from '../../../lib/fix-contenteditable-styles';
import { isMetaKey } from '../../../lib/is-meta-key';
import { RelativeTimePipe } from '../../../shared-extra/pipes/relative-time.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { IsUndefinedOrNullPipe } from '../../pipes/is-undefined-or-null.pipe';
import { VideoTimePercentToFrameCountPipe } from '../../pipes/video-time-percent-to-frame-count.pipe';
import { VideoTimePercentToStringPipe } from '../../pipes/video-time-percent-to-string.pipe';
import { Comment, CommentThread } from '../comments/comments.component';

@Component({
  selector: 'app-comment-item',
  standalone: true,
  // changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './comment-item.component.html',
  styleUrl: './comment-item.component.scss',
  imports: [
    AvatarComponent,
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    RelativeTimePipe,
    ButtonMenuItemComponent,
    TranslatePipe,
    WysiwygDirective,
    TooltipDirective,
    VideoTimePercentToStringPipe,
    VideoTimePercentToFrameCountPipe,
    DatePipe,
    IsUndefinedOrNullPipe,
  ],
})
export class CommentItemComponent implements OnChanges {
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (event.target['getAttribute']?.('name') === 'edit-comment') {
      if (event.key === 'Enter' && metaKey) this.saveEditing(event);
    }
  }
  @ViewChild('editComment') editComment: ElementRef<HTMLDivElement>;

  @Input() comment: Comment | CommentThread;
  @Input() accessLevel: GalleryAccessLevel;
  @Input() myId: string;
  @Input() expandReplies: boolean;
  @Input() asset: AssetDto;

  @Output() deleteComment = new EventEmitter<Comment | CommentThread>();
  @Output() resolveThread = new EventEmitter<boolean>();
  @Output() updateComment = new EventEmitter<string>();
  @Output() unselect = new EventEmitter<void>();

  thread = signal<CommentThread>(null);
  editing = signal(false);
  editingText = '';

  ngOnChanges(): void {
    this.thread.set('replies' in this.comment ? this.comment : null);
  }

  toggleResolved(event: Event) {
    event.stopPropagation();
    this.resolveThread.emit(!this.comment.resolved);
  }

  toggleEditing(event: Event) {
    this.editing.set(!this.editing());
    this.editingText = this.comment.text || '';
    event.stopPropagation();
    if (this.editing) setTimeout(() => this.editComment.nativeElement.focus());
  }

  cancelEditing(event: Event) {
    this.editing.set(false);
    event.stopPropagation();
  }

  saveEditing(event: Event) {
    const text = fixContentEditableStyles(this.editingText).trim();
    this.updateComment.emit(text);
    this.comment.text = text;
    this.editing.set(false);
    event.stopPropagation();
  }

  toggleReplies(event: Event) {
    if (this.expandReplies) {
      this.unselect.emit();
      event.stopPropagation();
    }
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }
}
